import { supabase } from "@/integrations/supabase/client";
import { ApiCallsAuditService } from "@/services/apiCallsAuditService";
import { LLMConfigService } from "@/services/llm/LLMConfigService";
import { ApiTokensService } from "@/hooks/api-tokens/service";
import { UnifiedLLMService } from "@/services/llm/UnifiedLLMService";

interface ChatCompletionParams {
  apiKey: string;
  model: string;
  messages: Array<{ 
    role: string; 
    content: string | Array<{ type: string; text?: string; image_url?: { url: string } }> 
  }>;
  source: string;
  brandId: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  skipConfigCheck?: boolean; // Flag to skip config check when called from UnifiedLLMService
}

interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export class OpenAIWrapper {
  async chatCompletion(params: ChatCompletionParams): Promise<OpenAIResponse> {
    const {
      apiKey: providedApiKey,
      model: providedModel,
      messages,
      source,
      brandId,
      temperature: providedTemperature,
      max_tokens: providedMaxTokens,
      stream = false,
      skipConfigCheck = false
    } = params;

    // Check for LLM configuration for this source
    let finalModel = providedModel;
    let finalApiKey = providedApiKey;
    let finalTemperature = providedTemperature;
    let finalMaxTokens = providedMaxTokens;
    let configUsed = false;
    let shouldSkipLogging = false; // Flag to skip logging if we routed to UnifiedLLMService
    let config: any = null; // Store config for later use

    try {
      // Skip config check if called from UnifiedLLMService (to avoid infinite loops)
      if (skipConfigCheck) {
        console.log(`[OpenAIWrapper] Skipping config check (called from UnifiedLLMService)`);
      } else {
        console.log(`[OpenAIWrapper] Checking config for source: "${source}", brandId: "${brandId}"`);
        console.log(`[OpenAIWrapper] Source type: ${typeof source}, BrandId type: ${typeof brandId}`);
        
        if (!brandId) {
          console.warn('[OpenAIWrapper] No brandId provided, skipping config lookup');
        } else {
          try {
            config = await LLMConfigService.getLLMConfig(brandId, source);
            console.log(`[OpenAIWrapper] Config lookup result:`, config ? {
              provider: config.provider,
              model: config.model,
              isActive: config.isActive,
              priority: config.priority,
              source: config.source,
              brandId: config.brandId
            } : 'No config found');
            
            if (!config) {
              console.warn(`[OpenAIWrapper] ⚠️ No config found for source="${source}" and brandId="${brandId}"`);
              console.warn(`[OpenAIWrapper] ⚠️ Please check: 1) Config exists in llm_source_configurations table, 2) source name matches exactly (case-sensitive), 3) brand_id matches, 4) is_active = true`);
            }
          } catch (configError) {
            console.error('[OpenAIWrapper] Error during config lookup:', configError);
            config = null;
          }
          
          if (config && config.isActive) {
            if (config.provider === 'openai') {
              // Config exists for OpenAI - use it
              finalModel = config.model;
              configUsed = true;
              
              // Get API key from tokens
              const apiTokens = await ApiTokensService.fetchTokens(brandId);
              if (apiTokens.openai) {
                finalApiKey = apiTokens.openai;
              }
              
              // Merge metadata from config
              if (config.metadata) {
                if (config.metadata.temperature !== undefined) {
                  finalTemperature = providedTemperature ?? config.metadata.temperature;
                }
                if (config.metadata.max_tokens !== undefined) {
                  finalMaxTokens = providedMaxTokens ?? config.metadata.max_tokens;
                }
              }
              
              console.log(`[OpenAIWrapper] ✅ Using configured LLM for source "${source}": ${config.provider}/${config.model}`);
            } else {
            // Config exists but for a different provider - route through UnifiedLLMService
            console.log(`[OpenAIWrapper] 🔄 Config exists for source "${source}" with provider "${config.provider}", routing through UnifiedLLMService for provider switch`);
            console.log(`[OpenAIWrapper] ⚠️ IMPORTANT: Returning early - OpenAIWrapper.logApiCall() will NOT be called. The actual API call will be logged by ${config.provider} wrapper.`);
            
            shouldSkipLogging = true; // Mark that we're routing, so don't log as OpenAI
            
            // Convert messages to UnifiedLLMService format and route
            const unifiedResponse = await UnifiedLLMService.callLLM({
              brandId,
              source,
              messages,
              temperature: providedTemperature,
              max_tokens: providedMaxTokens,
              stream
            });
            
            console.log(`[OpenAIWrapper] ✅ UnifiedLLMService returned response from ${unifiedResponse.provider}/${unifiedResponse.model}`);
            console.log(`[OpenAIWrapper] ✅ This call will be logged in api_calls_audit with platform="${unifiedResponse.provider}", NOT "openai"`);
            
            // Convert UnifiedLLMService response back to OpenAI format
            return {
              choices: [{
                message: {
                  content: unifiedResponse.content
                },
                finish_reason: unifiedResponse.finish_reason || 'stop'
              }],
              usage: unifiedResponse.usage
            };
            }
          } else {
            console.log(`[OpenAIWrapper] No active config found, using provided values: ${providedModel}`);
          }
        }
      }
    } catch (error) {
      // If config lookup fails, fall back to provided values
      console.warn('[OpenAIWrapper] ❌ Failed to fetch LLM config, using provided values:', error);
    }

    const startTime = Date.now();
    let statusCode = 0;
    let promptTokens = 0;
    let completionTokens = 0;
    let totalTokens = 0;
    let estimatedTokens = false;
    let responseData: OpenAIResponse | null = null;
    let errorMessage: string | undefined;

    try {
      // Prepare request body based on model
      const isNewerModel = finalModel.includes('gpt-5') || finalModel.includes('gpt-4.1') || finalModel.includes('o3') || finalModel.includes('o4');
      const requestBody: any = {
        model: finalModel,
        messages,
        stream
      };

      // Newer models use max_completion_tokens, legacy use max_tokens
      if (finalMaxTokens) {
        if (isNewerModel) {
          requestBody.max_completion_tokens = finalMaxTokens;
        } else {
          requestBody.max_tokens = finalMaxTokens;
        }
      }

      // Only add temperature for legacy models
      if (finalTemperature !== undefined && !isNewerModel) {
        requestBody.temperature = finalTemperature;
      }

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${finalApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      statusCode = response.status;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error?.message || response.statusText;
        
        // Log the error and re-throw (only if we didn't route through UnifiedLLMService)
        if (!shouldSkipLogging) {
          await this.logApiCall({
            source,
            brandId,
            model: finalModel,
            endpoint: '/v1/chat/completions',
            statusCode,
            duration: Date.now() - startTime,
            tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
            metadata: {
              error: errorMessage,
              request_failed: true,
              messages_count: messages.length,
              temperature: finalTemperature,
              max_tokens: finalMaxTokens,
              config_used: configUsed
            }
          });
        }

        throw new Error(`OpenAI API error (${statusCode}): ${errorMessage}`);
      }

      responseData = await response.json();

      // Extract token usage
      if (responseData.usage) {
        promptTokens = responseData.usage.prompt_tokens;
        completionTokens = responseData.usage.completion_tokens;
        totalTokens = responseData.usage.total_tokens;
      } else if (stream) {
        // For streaming, estimate tokens
        const content = responseData.choices[0]?.message?.content || '';
        completionTokens = Math.ceil(content.length / 4);
        promptTokens = Math.ceil(JSON.stringify(messages).length / 4);
        totalTokens = promptTokens + completionTokens;
        estimatedTokens = true;
      }

      // Calculate cost
      const costEstimate = ApiCallsAuditService.estimateCost(
        'openai',
        { prompt_tokens: promptTokens, completion_tokens: completionTokens, total_tokens: totalTokens },
        finalModel
      );

      // Log successful call (only if we didn't route through UnifiedLLMService)
      if (shouldSkipLogging) {
        console.log(`[OpenAIWrapper] ⚠️ SKIPPING LOG - This call was routed through UnifiedLLMService, logging should happen in the target provider wrapper`);
      } else {
        console.log(`[OpenAIWrapper] 📝 Logging API call to audit: platform=openai, source="${source}", model="${finalModel}", config_used=${configUsed}`);
        await this.logApiCall({
          source,
          brandId,
          model: finalModel,
          endpoint: '/v1/chat/completions',
          statusCode,
          duration: Date.now() - startTime,
          tokens: {
            prompt_tokens: promptTokens,
            completion_tokens: completionTokens,
            total_tokens: totalTokens,
            estimated_tokens: estimatedTokens
          },
          costEstimate,
          metadata: {
            messages_count: messages.length,
            temperature: finalTemperature,
            max_tokens: finalMaxTokens,
            finish_reason: responseData.choices[0]?.finish_reason,
            estimated_tokens: estimatedTokens,
            config_used: configUsed
          }
        });
      }

      return responseData;

    } catch (error) {
      // If we haven't logged yet, log the error
      if (!errorMessage) {
        await this.logApiCall({
          source,
          brandId,
          model: finalModel,
          endpoint: '/v1/chat/completions',
          statusCode: statusCode || 0,
          duration: Date.now() - startTime,
          tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
          metadata: {
            error: error instanceof Error ? error.message : 'Unknown error',
            request_failed: true,
            messages_count: messages.length,
            temperature: finalTemperature,
            max_tokens: finalMaxTokens,
            config_used: configUsed
          }
        });
      }

      throw error;
    }
  }

  private async logApiCall(data: {
    // NOTE: This method is only called when OpenAIWrapper makes the actual API call
    // If provider switching happens, this method is NOT called (returns early)
    source: string;
    brandId: string;
    model: string;
    endpoint: string;
    statusCode: number;
    duration: number;
    tokens: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      estimated_tokens?: boolean;
    };
    costEstimate?: number;
    metadata?: any;
  }) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      await ApiCallsAuditService.logApiCall({
        source_menu_item: data.source,
        platform: 'openai',
        endpoint: data.endpoint,
        method: 'POST',
        status_code: data.statusCode,
        duration_ms: data.duration,
        cost_estimate: data.costEstimate || 0,
        brand_id: data.brandId,
        model_used: data.model,
        metadata: {
          ...data.metadata,
          prompt_tokens: data.tokens.prompt_tokens,
          completion_tokens: data.tokens.completion_tokens,
          total_tokens: data.tokens.total_tokens
        }
      });
    } catch (error) {
      console.error('Failed to log API call:', error);
    }
  }
}
